import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, BarChart3, Bot, FileText, RefreshCw } from 'lucide-react';

const getTextFromHtml = (html) => {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc.body?.textContent?.trim() || '';
};

const getFriendlyError = (html) => {
  const text = getTextFromHtml(html);
  if (!text) return '';

  const jsonStart = text.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const parsed = JSON.parse(text.slice(jsonStart));
      return parsed?.error?.message || parsed?.message || text;
    } catch {
      return text;
    }
  }

  return text.replace(/^AI Error/i, '').trim();
};

const buildPreviewHtml = (reportHtml, chartHtml) => `
<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body {
        margin: 0;
        padding: 18px;
        color: #111827;
        background: #ffffff;
        font-family: Arial, sans-serif;
      }
      table {
        width: 100%;
        border-collapse: collapse;
      }
      th, td {
        border: 1px solid #e5e7eb;
        padding: 8px;
        text-align: left;
      }
      th {
        background: #f3f4f6;
      }
      .ai-chart {
        margin-bottom: 18px;
      }
    </style>
  </head>
  <body>
    ${chartHtml ? `<div class="ai-chart">${chartHtml}</div>` : ''}
    ${reportHtml || ''}
  </body>
</html>
`;

const AiReportDisplay = () => {
  const navigate = useNavigate();
  const [aiResponse, setAiResponse] = React.useState(null);
  const [reportHtml, setReportHtml] = React.useState('');
  const [chartHtml, setChartHtml] = React.useState('');
  const iframeRef = React.useRef(null);

  React.useEffect(() => {
    // AI display improvement: prefer the full API response when available.
    const storedResponse = sessionStorage.getItem('aiReportResponse');
    const storedHtml = sessionStorage.getItem('aiReportHtml');

    if (storedResponse) {
      try {
        const parsed = JSON.parse(storedResponse);
        setAiResponse(parsed);
        setReportHtml(parsed?.report_html || '');
        setChartHtml(parsed?.chart_html || '');
      } catch {
        setReportHtml(storedHtml || '');
      }
      sessionStorage.removeItem('aiReportResponse');
      sessionStorage.removeItem('aiReportHtml');
      return;
    }

    if (storedHtml) {
      setReportHtml(storedHtml);
      sessionStorage.removeItem('aiReportHtml');
    }
  }, []);

  const modelsUsed = aiResponse?.models_used || [];
  const isError =
    modelsUsed.includes('error') ||
    /AI Error/i.test(reportHtml) ||
    /"status"\s*:\s*"UNAVAILABLE"/i.test(reportHtml);
  const errorMessage = isError ? getFriendlyError(reportHtml) : '';
  const hasReport = Boolean(reportHtml || chartHtml);
  const previewHtml = buildPreviewHtml(reportHtml, chartHtml);

  React.useEffect(() => {
    if (!hasReport || isError || !iframeRef.current) return undefined;

    const blob = new Blob([previewHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    iframeRef.current.src = url;

    return () => URL.revokeObjectURL(url);
  }, [hasReport, isError, previewHtml]);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="sticky top-0 z-20 border-b border-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 px-4 py-3">
          {window.name !== 'aiReportPopup' && (
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft size={16} />
              Back
            </button>
          )}
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-purple-700 text-white">
            <Bot size={20} />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-gray-900">AI Generated Report</h1>
            <p className="text-xs text-gray-500">
              {modelsUsed.length > 0 ? `Model: ${modelsUsed.join(', ')}` : 'Generated from current report data'}
            </p>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl p-4">
        {isError ? (
          <div className="rounded-lg border border-red-200 bg-white shadow-sm">
            <div className="flex items-start gap-3 border-b border-red-100 bg-red-50 p-4">
              <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-md bg-red-100 text-red-700">
                <AlertTriangle size={20} />
              </div>
              <div>
                <h2 className="text-base font-semibold text-red-800">AI service is temporarily unavailable</h2>
                <p className="mt-1 text-sm text-red-700">
                  {errorMessage || 'The AI model could not generate the report right now. Please try again later.'}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 p-4">
              <button
                onClick={() => window.close()}
                className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Close
              </button>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <RefreshCw size={16} />
                Refresh
              </button>
            </div>
          </div>
        ) : hasReport ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <FileText size={16} />
                  Report
                </div>
                <p className="mt-1 text-xs text-gray-500">{reportHtml ? 'HTML report is ready' : 'No report HTML returned'}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <BarChart3 size={16} />
                  Chart
                </div>
                <p className="mt-1 text-xs text-gray-500">{chartHtml ? 'Chart output included' : 'No chart output returned'}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="text-sm font-semibold text-gray-700">Selected Table</div>
                <p className="mt-1 truncate text-xs text-gray-500">{aiResponse?.selected_table || 'Not specified'}</p>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
                <h2 className="text-sm font-semibold text-gray-800">Preview</h2>
              </div>
              <iframe
                ref={iframeRef}
                className="h-[calc(100vh-260px)] min-h-[520px] w-full bg-white"
                title="AI Report"
              />
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
            <Bot size={34} className="mx-auto mb-3 text-purple-700" />
            <h2 className="text-base font-semibold text-gray-800">No report content available</h2>
            <p className="mt-1 text-sm text-gray-500">Please ask the AI Assistant to generate a report again.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default AiReportDisplay;
